import { RootState } from '@/modules/ewwms/store';
import { ActionContext, ActionTree } from 'vuex';
import { Mutations, MutationTypes } from './mutations';
// import { Mutations as BestellungMutations, MutationTypes as BestellungMutationTypes } from "../Bestellung/mutations";
import { State } from './state';

import { useEwwmsCommonStore } from '../../ewwmsCommonStore';
import { ActionTypes as BestellungActionTypes } from '../Bestellung/actions';
import { ActionTypes as BestandActionTypes } from '../Bestand/actions';

import ApiService from '@/modules/ewwms/services/ApiService';

import {
  CreateUmfuhrRequest,
  Umfuhr,
  Umfuhrpos,
  SetListeartikelUmfuhrRequest,
  UpdateUmfuhrLkwKennzeichenRequest,
} from '@/modules/ewwms/models/Lager/UmfuhrModels';
import { Listeartikel } from '@/modules/ewwms/models/Lager/ListeartikelModels';

export enum ActionTypes {
  GetUmfuhr = 'GETUMFUHR',
  GetUpdateUmfuhr = 'GETUPDATEUMFUHR',
  CreateUmfuhr = 'CREATEUMFUHR',
  UpdateUmfuhr = 'UPDATEUMFUHR',
  SetUmfuhrLkwKennzeichen = 'SETUMFUHRLKWKENNZEICHEN',
  DeleteUmfuhr = 'DELETEUMFUHR',
  ResetUmfuhr = 'RESETUMFUHR',

  SetUmfuhrVerladen = 'SETBUMFUHRVERLADEN',
  SetUmfuhrEntladen = 'SETBUMFUHRENTLADEN',
  GetUmfuhrPaletten = 'GETUMFUHRPALETTEN',
  SetListeartikelUmfuhr = 'SETLISTEARTIKELUMFUHR',
  SetListeartikelUmfuhrEntladen = 'SETLISTEARTIKELUMFUHRENTLADEN',

  GetUmfuhrpos = 'GETUMFUHRPOS',
  GetUmfuhrposByUmfuhr = 'GETUMFUHRPOSBYUMFUHR',
  CreateUmfuhrpos = 'CREATEUMFUHRPOS',
  CreateUmfuhrposRange = 'CREATEUMFUHRPOSRANGE',
  UpdateUmfuhrpos = 'UPDATEUMFUHRPOS',
  DeleteUmfuhrpos = 'DELETEUMFUHRPOS',
}

type ActionArguments = Omit<ActionContext<State, RootState>, 'commit'> & {
  commit<K extends keyof Mutations>(key: K, payload?: Parameters<Mutations[K]>[1]): ReturnType<Mutations[K]>;
};

export type Actions = {
  [ActionTypes.GetUmfuhr](context: ActionArguments, data: Date | null): Promise<void>;
  [ActionTypes.GetUpdateUmfuhr](context: ActionArguments, data: Date | null): Promise<void>;
  [ActionTypes.CreateUmfuhr](context: ActionArguments, data: CreateUmfuhrRequest): Promise<void>;
  [ActionTypes.UpdateUmfuhr](context: ActionArguments, data: Umfuhr): Promise<void>;
  [ActionTypes.SetUmfuhrLkwKennzeichen](context: ActionArguments, data: UpdateUmfuhrLkwKennzeichenRequest): Promise<void>;
  [ActionTypes.DeleteUmfuhr](context: ActionArguments, id: number): Promise<void>;
  [ActionTypes.ResetUmfuhr](context: ActionArguments): void;

  [ActionTypes.SetUmfuhrVerladen](context: ActionArguments, data: number): Promise<void>;
  [ActionTypes.SetUmfuhrEntladen](context: ActionArguments, data: number): Promise<void>;
  [ActionTypes.GetUmfuhrPaletten](context: ActionArguments, data: number): Promise<void>;
  [ActionTypes.SetListeartikelUmfuhr](context: ActionArguments, data: SetListeartikelUmfuhrRequest): Promise<void>;
  [ActionTypes.SetListeartikelUmfuhrEntladen](context: ActionArguments, data: SetListeartikelUmfuhrRequest): Promise<void>;

  [ActionTypes.GetUmfuhrpos](context: ActionArguments): Promise<void>;
  [ActionTypes.GetUmfuhrposByUmfuhr](context: ActionArguments, id: number): Promise<void>;
  [ActionTypes.CreateUmfuhrpos](context: ActionArguments, data: Umfuhrpos): Promise<void>;
  [ActionTypes.CreateUmfuhrposRange](context: ActionArguments, data: Array<Umfuhrpos>): Promise<void>;
  [ActionTypes.UpdateUmfuhrpos](context: ActionArguments, data: Umfuhrpos): Promise<void>;
  [ActionTypes.DeleteUmfuhrpos](context: ActionArguments, id: number): Promise<void>;
};

export const actions: ActionTree<State, RootState> & Actions = {
  //#region Umfuhr
  async [ActionTypes.GetUmfuhr]({ commit }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.getUmfuhrLoading = true;
    status.umfuhrErrorMsg = '';
    commonStore.setStatus(status);
    commit(MutationTypes.SetUmfuhr, new Array<Umfuhr>());

    return ApiService.getAllUmfuhren(data)
      .then((res) => {
        const data: Array<Umfuhr> = res.data;
        commit(MutationTypes.SetUmfuhr, data);
      })
      .catch((error) => {
        status.umfuhrErrorMsg = error.response;
        commonStore.setStatus(status);
      })
      .finally(() => {
        status.getUmfuhrLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.GetUpdateUmfuhr]({ commit }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.umfuhrErrorMsg = '';
    commonStore.setStatus(status);

    return ApiService.getAllUmfuhren(data)
      .then((res) => {
        const data: Array<Umfuhr> = res.data;
        commit(MutationTypes.SetUmfuhr, data);
      })
      .catch((error) => {
        status.umfuhrErrorMsg = error.response;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.CreateUmfuhr]({ commit }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.createUmfuhrLoading = true;
    status.umfuhrErrorMsg = '';
    commonStore.setStatus(status);

    return ApiService.createAuftrag(data)
      .then((res) => {
        const data: Umfuhr = res.data;
        commit(MutationTypes.AddUmfuhr, data);
      })
      .catch((error) => {
        status.umfuhrErrorMsg = error.response.data;
        commonStore.setStatus(status);
      })
      .finally(() => {
        status.createUmfuhrLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.UpdateUmfuhr]({ commit }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.updateUmfuhrLoading = true;
    status.umfuhrErrorMsg = '';
    commonStore.setStatus(status);

    return ApiService.updateAuftrag(data)
      .then((res) => {
        const data: Umfuhr = res.data;
        commit(MutationTypes.UpdateUmfuhr, data);
      })
      .catch((error) => {
        status.umfuhrErrorMsg = error.response;
        commonStore.setStatus(status);
      })
      .finally(() => {
        status.updateUmfuhrLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.SetUmfuhrLkwKennzeichen]({ commit }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.umfuhrErrorMsg = '';
    status.updateUmfuhrLoading = true;
    commonStore.setStatus(status);

    return ApiService.updateUmfuhrLkwKennzeichen(data)
      .then((res) => {
        const umfuhr: Umfuhr = res.data;
        commit(MutationTypes.UpdateUmfuhr, umfuhr);
      })
      .catch((error) => {
        console.log(error);
        status.umfuhrErrorMsg = error.toString();
      })
      .finally(() => {
        status.updateUmfuhrLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.SetUmfuhrVerladen]({ commit }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.umfuhrErrorMsg = '';
    status.updateUmfuhrLoading = true;
    commonStore.setStatus(status);

    return ApiService.setUmfuhrVerladen(data)
      .then((res) => {
        const bestellung: Umfuhr = res.data;
        commit(MutationTypes.UpdateUmfuhr, bestellung);
      })
      .catch((error) => {
        console.log(error);
        status.umfuhrErrorMsg = error.toString();
      })
      .finally(() => {
        status.updateUmfuhrLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.SetUmfuhrEntladen]({ commit }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.umfuhrErrorMsg = '';
    status.updateUmfuhrLoading = true;
    commonStore.setStatus(status);

    return ApiService.setUmfuhrEntladen(data)
      .then((res) => {
        const bestellung: Umfuhr = res.data;
        commit(MutationTypes.UpdateUmfuhr, bestellung);
      })
      .catch((error) => {
        console.log(error);
        status.umfuhrErrorMsg = error.toString();
      })
      .finally(() => {
        status.updateUmfuhrLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.DeleteUmfuhr]({ commit }, id) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.deleteUmfuhrLoading = true;
    status.umfuhrErrorMsg = '';
    commonStore.setStatus(status);

    return ApiService.deleteAuftrag(id)
      .then(() => {
        commit(MutationTypes.DeleteUmfuhr, id);
      })
      .catch((error) => {
        status.umfuhrErrorMsg = error.response;
        commonStore.setStatus(status);
      })
      .finally(() => {
        status.deleteUmfuhrLoading = false;
        commonStore.setStatus(status);
      });
  },

  [ActionTypes.ResetUmfuhr]({ commit }) {
    commit(MutationTypes.SetUmfuhr, new Array<Umfuhr>());
  },
  //#endregion

  //#region UmfuhrPaletten
  async [ActionTypes.GetUmfuhrPaletten]({ dispatch }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.umfuhrPalettenErrorMsg = '';
    status.getUmfuhrPalettenLoading = true;
    commonStore.setStatus(status);

    return ApiService.getUmfuhrPaletten(data)
      .then((res) => {
        const paletten: Array<Listeartikel> = res.data;
        dispatch(BestellungActionTypes.AddOrUpdatePaletten, paletten);
        // commit(BestellungMutationTypes.AddPaletten, paletten)
      })
      .catch((error) => {
        console.log(error);
        status.umfuhrPalettenErrorMsg = error.toString();
      })
      .finally(() => {
        status.getUmfuhrPalettenLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.SetListeartikelUmfuhr]({ dispatch }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.umfuhrPalettenErrorMsg = '';
    status.updateUmfuhrPalettenLoading = true;
    commonStore.setStatus(status);

    return ApiService.setListeartikelUmfuhr(data)
      .then((res) => {
        const paletten: Listeartikel = res.data;
        dispatch(BestellungActionTypes.AddOrUpdatePalette, paletten);
        dispatch(BestandActionTypes.RemoveBestandLocal, paletten.id);

        // commit(MutationTypes.AddOrUpdatePalette, paletten)
      })
      .catch((error) => {
        console.log(error);
        if (error.response.status == 400) {
          status.umfuhrPalettenErrorMsg = error.response.data.title;
        } else {
          status.umfuhrPalettenErrorMsg = error.toString();
        }
      })
      .finally(() => {
        status.updateUmfuhrPalettenLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.SetListeartikelUmfuhrEntladen]({ dispatch }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.umfuhrPalettenErrorMsg = '';
    status.updateUmfuhrPalettenLoading = true;
    commonStore.setStatus(status);

    return ApiService.setListeartikelUmfuhrEntladen(data)
      .then((res) => {
        const paletten: Listeartikel = res.data;
        dispatch(BestellungActionTypes.AddOrUpdatePalette, paletten);
      })
      .catch((error) => {
        console.log(error);
        if (error.response.status == 400) {
          status.umfuhrPalettenErrorMsg = error.response.data.title;
        } else {
          status.umfuhrPalettenErrorMsg = error.toString();
        }
      })
      .finally(() => {
        status.updateUmfuhrPalettenLoading = false;
        commonStore.setStatus(status);
      });
  },
  //#endregion

  //#region AuftragPos
  async [ActionTypes.GetUmfuhrpos]({ commit }) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.getUmfuhrposLoading = true;
    status.umfuhrposErrorMsg = '';
    commonStore.setStatus(status);

    return ApiService.getAllUmfuhrpos()
      .then((res) => {
        const data: Array<Umfuhrpos> = res.data;
        commit(MutationTypes.SetUmfuhrPos, data);
      })
      .catch((error) => {
        status.umfuhrposErrorMsg = error.response;
        commonStore.setStatus(status);
      })
      .finally(() => {
        status.getUmfuhrposLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.GetUmfuhrposByUmfuhr]({ commit }, id) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.getUmfuhrposLoading = true;
    status.umfuhrposErrorMsg = '';
    commonStore.setStatus(status);

    return ApiService.getAllUmfuhrposByUmfuhrid(id)
      .then((res) => {
        const data: Array<Umfuhrpos> = res.data;
        commit(MutationTypes.AddUmfuhrPosRange, data);
      })
      .catch((error) => {
        status.umfuhrposErrorMsg = error.response;
        commonStore.setStatus(status);
      })
      .finally(() => {
        status.getUmfuhrposLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.CreateUmfuhrpos]({ commit }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.createUmfuhrposLoading = true;
    status.umfuhrposErrorMsg = '';
    commonStore.setStatus(status);

    return ApiService.createUmfuhrpos(data)
      .then((res) => {
        const data: Umfuhrpos = res.data;
        commit(MutationTypes.AddUmfuhrPos, data);
      })
      .catch((error) => {
        status.umfuhrposErrorMsg = error.response;
        commonStore.setStatus(status);
      })
      .finally(() => {
        status.createUmfuhrposLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.CreateUmfuhrposRange]({}, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.createUmfuhrposLoading = true;
    status.umfuhrposErrorMsg = '';
    commonStore.setStatus(status);

    return ApiService.createUmfuhrposRange(data)
      .then(() => {
        // const data: Array<AuftragPos> = res.data
        // commit(MutationTypes.AddAuftragPosRange, data)
      })
      .catch((error) => {
        status.umfuhrposErrorMsg = error.response;
        commonStore.setStatus(status);
      })
      .finally(() => {
        status.createUmfuhrposLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.UpdateUmfuhrpos]({ commit }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.updateUmfuhrposLoading = true;
    status.umfuhrposErrorMsg = '';
    commonStore.setStatus(status);

    return ApiService.updateUmfuhrpos(data)
      .then((res) => {
        const data: Umfuhrpos = res.data;
        commit(MutationTypes.UpdateUmfuhrPos, data);
      })
      .catch((error) => {
        status.umfuhrposErrorMsg = error.response;
        commonStore.setStatus(status);
      })
      .finally(() => {
        status.updateUmfuhrposLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.DeleteUmfuhrpos]({ commit }, id) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.deleteUmfuhrposLoading = true;
    status.umfuhrposErrorMsg = '';
    commonStore.setStatus(status);

    return ApiService.deleteUmfuhrpos(id)
      .then(() => {
        commit(MutationTypes.DeleteUmfuhrPos, id);
      })
      .catch((error) => {
        status.umfuhrposErrorMsg = error.response;
        commonStore.setStatus(status);
      })
      .finally(() => {
        status.deleteUmfuhrposLoading = false;
        commonStore.setStatus(status);
      });
  },
  //#endregion
};
